import axios, { AxiosRequestConfig, AxiosError } from "axios";
import { CompassError } from "../../types/errorTypes";
import { baseUrl } from "./config";

export class APIClient {
  private baseURL: string;

  constructor(baseURL: string = baseUrl) {
    this.baseURL = baseURL;
  }

  // Async GET request method
  get = async <T>(
    endpoint: string,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    try {
      const response = await axios.get<T>(`${this.baseURL}${endpoint}`, config);

      return response.data;
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  };

  async post<T, D>(
    endpoint: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await axios.post<T>(
        `${this.baseURL}${endpoint}`,
        data,
        config
      );
      return response.data; // Return the response data if the request is successful
    } catch (error) {
      throw this.handleError(error as AxiosError); // Throw an error if something goes wrong
    }
  }

  async poll<T, D>(
    endpoint: string,
    data: D,
    condition: (response: T) => boolean,
    conditionFail?: (response: T) => boolean,
    config?: AxiosRequestConfig,
    interval = 5000,
    maxAttempts = 60
  ): Promise<T | CompassError> {
    // Only resolves with T
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const intervalId = setInterval(async () => {
        try {
          const response = await axios.post<T>(
            `${this.baseURL}${endpoint}`,
            data,
            config
          );

          if (condition(response.data)) {
            clearInterval(intervalId);
            resolve(response.data);
          } else if (conditionFail && conditionFail(response.data)) {
            clearInterval(intervalId);
            reject(new Error("Process failed"));
          } else if (++attempts >= maxAttempts) {
            clearInterval(intervalId);
            reject(new Error("Max polling attempts reached"));
          }
        } catch (error) {
          clearInterval(intervalId);
          reject(this.handleError(error as AxiosError)); // Always reject with errors
        }
      }, interval);
    });
  }

  public handleError = (error: AxiosError): CompassError => {
    console.error("API call failed:", error);
    let errorDetails: CompassError = {
      step: "Unknown",
      error_string: "Unknown error",
      error_user_title: "Error : Unhandled",
      error_user_message: "An unexpected error occurred",
    };

    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      const responseData = error.response.data as Record<string, any>; //eslint-disable-line
      console.error("Status:", status);

      if (typeof responseData === "object" && responseData !== null) {
        if ("message" in responseData && "details" in responseData) {
          console.error("Error Message:", responseData.message);
          if ("error" in responseData.details) {
            errorDetails = responseData.details.error;
            console.error("Error Details:", errorDetails);
          } else {
            console.error(
              "Error Details are missing in the structured response"
            );
          }
        } else {
          console.error("Data:", responseData);
        }
      } else {
        console.error("Response data is not an object:", responseData);
      }

      console.error("Headers:", error.response.headers);
    } else {
      console.error("Unexpected Error:", error);
    }

    return errorDetails;
  };
}
